import { FC, PropsWithChildren, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useAtom } from "jotai"
import {
	fetchMaintenanceModeStatusState,
	maintenanceModeAtom,
	type MaintenanceModeState,
} from "@/store/maintenance-mode"
import { isMaintenanceBypassEnabled } from "@/utils/measurement/feature-flag"

const ACTIVE_MAINTENANCE_POLL_INTERVAL_MS = 10000

interface RefreshStatusOptions {
	shouldApplyState?: () => boolean
}

interface MaintenanceModeGateProps {
	maintenanceStatusUrl?: string
}

export const MaintenanceModeView: FC<{ state: MaintenanceModeState }> = ({ state }) => {
	const { t } = useTranslation("components")
	return (
		<div
			aria-label={t("maintenanceModeGate.ariaMaintenanceMode")}
			aria-live="polite"
			className="relative min-h-screen bg-white text-center font-proxima text-[#333333]"
			role="status"
		>
			<main className="absolute left-0 top-[35%] w-full px-[30px]">
				<h1 className="m-0 text-[16px] font-bold uppercase leading-normal text-[#555555] antialiased sm:text-[24px]">
					{t("maintenanceModeGate.plannedMaintenance")}
				</h1>
				<p className="mx-auto mt-[50px] max-w-[500px] text-[14px] leading-[21px] text-[#333333]">
					{state.message}
				</p>
			</main>
		</div>
	)
}

export const MaintenanceModeGate: FC<PropsWithChildren<MaintenanceModeGateProps>> = ({
	children,
	maintenanceStatusUrl,
}) => {
	const [maintenanceModeState, setMaintenanceModeState] = useAtom(maintenanceModeAtom)
	const hasStatusConfig = Boolean(maintenanceStatusUrl)

	const refreshStatus = useCallback(
		async ({ shouldApplyState = () => true }: RefreshStatusOptions = {}) => {
			const result = await fetchMaintenanceModeStatusState(maintenanceStatusUrl)

			if (!result.ok || !shouldApplyState()) {
				return
			}

			if (result.state.enabled && isMaintenanceBypassEnabled()) {
				return
			}

			setMaintenanceModeState(result.state)
		},
		[setMaintenanceModeState, maintenanceStatusUrl],
	)

	useEffect(() => {
		if (!hasStatusConfig) {
			return
		}

		let isCancelled = false
		void refreshStatus({ shouldApplyState: () => !isCancelled })

		return () => {
			isCancelled = true
		}
	}, [hasStatusConfig, refreshStatus])

	useEffect(() => {
		if (!hasStatusConfig || !maintenanceModeState.enabled) {
			return
		}

		let isCancelled = false
		const intervalId = globalThis.setInterval(() => {
			void refreshStatus({ shouldApplyState: () => !isCancelled })
		}, ACTIVE_MAINTENANCE_POLL_INTERVAL_MS)

		return () => {
			isCancelled = true
			globalThis.clearInterval(intervalId)
		}
	}, [hasStatusConfig, maintenanceModeState.enabled, refreshStatus])

	if (maintenanceModeState.enabled) {
		return <MaintenanceModeView state={maintenanceModeState} />
	}

	return <>{children}</>
}
